import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Warning, ChartLine } from "@phosphor-icons/react";

/**
 * Sprint countdown banner shown on the Kanban when an active sprint exists.
 * Displays remaining time, progress percentage, and turns red when overdue.
 */
export default function SprintCountdownBanner({ sprint, projectId, multipleActive }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!sprint) return null;

  const endStr = sprint.end_date;
  let endTs = null;
  if (endStr) {
    // end_date is "YYYY-MM-DD" — treat end-of-day
    endTs = new Date(endStr + "T23:59:59").getTime();
  }
  const msLeft = endTs ? endTs - now : null;
  const overdue = msLeft !== null && msLeft < 0;
  const daysLeft = msLeft !== null ? Math.floor(Math.abs(msLeft) / 86400000) : null;
  const hoursLeft = msLeft !== null ? Math.floor((Math.abs(msLeft) % 86400000) / 3600000) : null;
  const critical = msLeft !== null && msLeft >= 0 && msLeft < 86400000; // < 1 day

  const totalPts = sprint.total_points || 0;
  const donePts = sprint.done_points || 0;
  const pct = totalPts > 0 ? Math.round((donePts / totalPts) * 100) : 0;

  const styleFor = overdue
    ? { bg: "bg-[#FEF2F2]", border: "border-[#DC2626]", text: "text-[#7F1D1D]", accent: "text-[#DC2626]" }
    : critical
      ? { bg: "bg-[#FFFBEB]", border: "border-[#D97706]", text: "text-[#7C2D12]", accent: "text-[#D97706]" }
      : { bg: "bg-[#EFF6FF]", border: "border-[#002FA7]", text: "text-[#0A0A0A]", accent: "text-[#002FA7]" };

  return (
    <div>
      {multipleActive && (
        <div className="border-b border-[#DC2626] bg-[#FEE2E2] px-8 lg:px-12 py-2 text-xs text-[#7F1D1D] flex items-center gap-2" data-testid="multi-active-warning">
          <Warning size={14} weight="fill" className="text-[#DC2626]" />
          <b>Warning:</b> multiple sprints marked ACTIVE in this project — this should never happen. Complete or cancel all but one from the Sprints page.
        </div>
      )}
      <div className={`border-b ${styleFor.border} ${styleFor.bg} px-8 lg:px-12 py-3 flex items-center gap-4 flex-wrap`} data-testid="sprint-countdown">
        <Flag size={16} weight="bold" className={styleFor.accent} />
        <div className={`text-sm font-semibold ${styleFor.text}`}>
          <span className="font-mono-mini uppercase text-[10px] mr-1 text-muted-foreground">Active sprint</span>
          {sprint.name}
        </div>
        {msLeft !== null && (
          <div className={`text-sm font-bold ${styleFor.accent}`} data-testid="sprint-countdown-text">
            {overdue ? `OVERDUE by ${daysLeft}d ${hoursLeft}h` : `${daysLeft}d ${hoursLeft}h left`}
          </div>
        )}
        {sprint.end_date && (
          <div className="text-xs text-muted-foreground font-mono-mini">
            ends {sprint.end_date}
          </div>
        )}
        {totalPts > 0 && (
          <div className="flex items-center gap-2 min-w-[180px]">
            <div className="h-1.5 flex-1 bg-white/70 overflow-hidden border border-border">
              <div className={overdue || critical ? "h-full bg-[#DC2626]" : "h-full bg-[#002FA7]"} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs font-mono-mini text-muted-foreground">{donePts}/{totalPts} pts · {pct}%</div>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <Link
            to={`/projects/${projectId}/sprints/${sprint.id}/report`}
            className="text-xs border border-border bg-white px-2 py-1 hover:bg-muted flex items-center gap-1"
            data-testid="sprint-report-link"
          >
            <ChartLine size={12} weight="bold" /> Report
          </Link>
          <Link
            to={`/projects/${projectId}/sprints`}
            className="text-xs border border-border bg-white px-2 py-1 hover:bg-muted"
          >
            Manage sprints →
          </Link>
        </div>
      </div>
    </div>
  );
}
